import { motion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { STUDIO_BG } from "@/lib/data";

export default function VideoHero() {
 return (
 <section
 data-testid="hero-section"
 className="relative min-h-[92vh] w-full overflow-hidden grain"
 >
 {/* Background */}
 <div
 className="absolute inset-0 bg-cover bg-center"
 style={{ backgroundImage: `url(${STUDIO_BG})` }}
 />
 <div className="absolute inset-0 bg-gradient-to-b from-ink-900/90 via-ink-900/75 to-ink-900" />
 <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/70 to-transparent" />

 {/* Content */}
 <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-16 md:pb-20 min-h-[92vh] flex flex-col justify-end">
 <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-8">
 {/* Left. Copy */}
 <motion.div
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6 }}
 className="flex-1 max-w-3xl"
 >
 <p
 data-testid="hero-brand-wordmark"
 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-normal uppercase text-white mb-3"
 >
 Underground-Mix
 </p>

 <p className="font-display text-xl md:text-2xl lg:text-3xl tracking-normal uppercase text-amber font-bold mb-8">
 A premium sync community.
 </p>

 <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] font-bold leading-[1.05] tracking-normal">
 Access means nothing{" "}
 <span className="text-white/40">
 if the songs aren't built to{" "}
 </span>
 <span className="text-amber italic">win.</span>
 </h1>

 <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/70 leading-relaxed">
 We turn writers, artists and producers into creators of premium
 songs that impact the sync landscape. Then we bring real
 opportunities to match.
 </p>

 <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
 <Link
 to="/access"
 data-testid="hero-cta-primary"
 className="group inline-flex items-center justify-center gap-3 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-sm px-7 py-4 rounded-sm transition-all"
 >
 Explore Offers
 <ArrowRight
 size={16}
 className="transition-transform group-hover:translate-x-1"
 />
 </Link>
 <button
 type="button"
 data-testid="hero-cta-ai"
 onClick={() =>
 window.dispatchEvent(new CustomEvent("open-um-chat"))
 }
 className="group inline-flex items-center justify-center gap-3 bg-transparent border border-amber/60 hover:border-amber hover:bg-amber/10 text-amber font-bold uppercase tracking-widest text-sm px-7 py-4 rounded-sm transition-all"
 >
 <MessageCircle size={16} />
 Ask Our AI Sync Expert
 </button>
 </div>
 <p className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-white/45">
 Free. Trained on real placements. Answers in seconds.
 </p>
 </motion.div>

 {/* Right. Static logo emblem (centered with copy) */}
 <motion.div
 initial={{ opacity: 0, scale: 0.92 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ duration: 0.9, ease: "easeOut" }}
 data-testid="hero-feature-pane"
 className="hidden lg:flex relative shrink-0 w-[32rem] xl:w-[38rem] aspect-square items-center justify-center"
 >
 <div className="absolute inset-0 bg-gradient-radial from-amber/10 to-transparent blur-3xl pointer-events-none" />
 <img
 src="/um-logo.png"
 alt="Underground-Mix"
 data-testid="hero-logo-emblem"
 className="relative w-full h-full object-contain drop-shadow-[0_0_60px_rgba(34,197,94,0.2)]"
 />
 </motion.div>
 </div>
 </div>
 </section>
 );
}
